<template>
  <div class="container mt-4">
    <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2 p-1">
      <h2 class="mb-0 main-title">Assets</h2>
      <div class="d-flex align-items-center gap-2">
        <button class="btn btn-md btn-primary fs-11 fw-normal hover-white" @click="openCreateModal('createModal')">
          <i class="fa fa-plus fs-11"></i> Add New Asset
        </button>

        <button class="btn btn-md btn-outline-secondary hover-white fs-11 fw-normal"
          @click="$router.push({ name: 'AssetImport' })">
          <i class="fa fa-upload fs-11"></i> Import Assets
        </button>
      </div>
    </div>

    <!-- Filters Bar -->
    <div class="filters-wrapper">
      <div class="filters-bar" id="filtersBar"></div>
      <button @click="resetAllFilters" class="btn btn-sm btn-outline-danger reset-btn fs-12 hover-white">
        <span class="material-symbols-outlined fs-13">undo</span> Reset
      </button>
    </div>

    <!-- Dropdown Template -->
    <div id="genericFilterDropdownTemplate" style="display: none;">
      <div class="generic-filter-dropdown">
        <div class="dropdown-header">
          <input type="text" class="filter-search-input" placeholder="Search...">
        </div>
        <div class="dropdown-body filter-options-container"></div>
        <div class="dropdown-footer">
          <button class="filter-clear-btn">Clear</button>
          <button class="filter-apply-btn">Apply</button>
        </div>
      </div>
    </div>

    <div class="card p-1 bg-white">

      <!-- Modal -->
      <FormModal ref="createModal" title="Create New Asset" id="create_asset_form_modal">
        <CreateAsset ref="CreateAssetForm" @created="onCreated" @close="closeCreateModal('createModal')" />
      </FormModal>
      <FormModal ref="editModal" title="Edit Asset" id="edit_asset_form_modal">
        <EditAsset v-if="editingAsset" :assetId="editingAsset.external_id" @updated="onCreated"
          @close="closeCreateModal('editModal')" />
      </FormModal>
      <!-- Loading State -->
      <LoadingBar v-if="loading" message="Loading Assets..." />

      <!-- Assets Table -->
      <div v-else class="table-wrapper">
        <ListTable>
          <colgroup>
            <!-- <col style="width: 5%"> SR# -->
            <col style="width: 30%"> <!-- Asset -->
            <col style="width: 15%"> <!-- Category -->
            <col style="width: 15%"> <!-- Criticality -->
            <col style="width: 15%"> <!-- Materials -->
            <col style="width: 15%"> <!-- Vendors -->
            <col style="width: 10%"> <!-- Action -->
          </colgroup>
          <thead>
            <tr>
              <!-- <th>SR#</th> -->

              <th @click="sortBy('name')" class="sortable">
                Asset <i :class="getSortIcon('name')"></i>
              </th>

              <th @click="sortBy('asset_category_id')" class="sortable">
                Category
              </th>

              <th @click="sortBy('criticality')" class="sortable">
                Criticality <i :class="getSortIcon('criticality')"></i>
              </th>

              <th>Materials</th>
              <th>Vendors</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="asset in assets" :key="asset.external_id">

              <!-- SR#
              <td>
                {{ index + 1 }}
          </td> -->

              <!-- Asset -->
              <td class="align-middle">
                <div class="d-flex align-items-center gap-2" @click.prevent="viewDetails(asset)">
                  <div
                    class="d-flex align-items-center justify-content-center bg-primary-subtle border border-primary-subtle rounded-3"
                    style="width: 40px; height: 40px; min-width: 40px;">
                    <span class="material-symbols-outlined text-primary fs-10">inventory_2</span>
                  </div>

                  <div>
                    <div class="text-dark">{{ asset.name }}</div>
                    <div class="d-flex align-items-center gap-2">
                      <small class="text-muted">#{{ asset.serial_number }}</small>
                    </div>
                  </div>
                </div>
              </td>

              <!-- Category -->
              <td>
                {{ asset.get_category?.name || '—' }}
              </td>

              <!-- Criticality -->
              <td>
                <span v-if="asset.criticality" class="crit-badge" :class="{
                  'crit-high': asset.criticality.toLowerCase() === 'high',
                  'crit-med': asset.criticality.toLowerCase() === 'medium',
                  'crit-low': asset.criticality.toLowerCase() === 'low',
                }">
                  {{ asset.criticality }}
                </span>
                <span v-else class="text-muted">—</span>
              </td>

              <!-- Materials -->
              <td>
                <ItemsDropdown :items="asset.get_materials" label="Materials" serial-key="external_id" icon="flask"
                  row-icon="flask" />
              </td>

              <!-- Vendors -->
              <td>
                <ItemsDropdown :items="asset.get_vendors" label="Vendors" serial-key="external_id"
                  :name-keys="['first_name', 'last_name']" icon="handshake" row-icon="user-tie" />
              </td>

              <!-- Action -->
              <td>
                <TableActionDropdown>
                  <li> <a class="dropdown-item" @click.prevent="viewDetails(asset)"> <i class="fa fa-eye text-info"></i>
                      <span>Profile</span> </a> </li>
                  <li> <a class="dropdown-item" @click.prevent="editAsset(asset)"> <i
                        class="fa fa-pencil text-primary"></i> <span>Edit</span> </a> </li>
                 
                    <hr class="dropdown-divider" />
             
                  <li> <a class="dropdown-item item-danger" @click.prevent="deleteAsset(asset)"> <i
                        class="fa fa-trash"></i> <span>Delete</span> </a> </li>
                </TableActionDropdown>
              </td>

            </tr>
            <tr v-if="assets.length === 0">
              <td colspan="5" class="text-center py-4 text-muted">No Assets Found</td>
            </tr>
          </tbody>
        </ListTable>
      </div>

      <!-- Pagination -->
      <Pagination :pagination="pagination" @page-change="changePage" />

    </div>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import Pagination from '@/components/utils/PaginationBar.vue'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import FormModal from '@/components/utils/FormModal.vue'
import CreateAsset from '@/views/Asset/CreateAsset.vue'
import ListTable from '@/components/utils/ListTable.vue'
import TableActionDropdown from '@/components/TableActionDropdown.vue'
import EditAsset from '@/views/Asset/EditAsset.vue'
import ItemsDropdown from '@/components/AttributesDropdown.vue'
export default {
  name: 'AssetList',
  components: {
    Pagination,
    LoadingBar,
    FormModal,
    CreateAsset,
    ListTable,
    TableActionDropdown,
    EditAsset,
    ItemsDropdown,
  },
  data() {
    return {
      assets: [],
      filteredData: [],
      pagination: {
        total_rows: 0,
        per_page: 20,
        current_page: 1,
        total_pages: 1
      },
      loading: false,

      sortColumn: 'name',
      sortOrder: 'asc',

      filterManager: null,
      currentFilters: {},

      dropdownCategories: [],   // 👈 categories list
      dropdownMaterials: [],    // 👈 materials list
    }
  },
  computed: {
    sortedData() {
      let result = [...this.filteredData]
      if (this.sortColumn) {
        result.sort((a, b) => {
          let valA = a[this.sortColumn] ?? ''
          let valB = b[this.sortColumn] ?? ''
          if (this.sortColumn === 'id') {
            valA = parseInt(String(valA).replace('#', '')) || 0
            valB = parseInt(String(valB).replace('#', '')) || 0
          }
          if (typeof valA === 'string') valA = valA.toLowerCase()
          if (typeof valB === 'string') valB = valB.toLowerCase()
          if (valA < valB) return this.sortOrder === 'asc' ? -1 : 1
          if (valA > valB) return this.sortOrder === 'asc' ? 1 : -1
          return 0
        })
      }
      return result
    },
  },
  async mounted() {
    this.fetchList()

    // Wait for both dropdown data to load
    await Promise.all([
      this.get_categories(),
      this.get_materials()
    ])

    this.$nextTick(() => {
      this.initFilters()
    })
  },
  methods: {
    openCreateModal(ref) {
      this.$refs[ref].showModal()
    },
    closeCreateModal(ref) {
      this.$refs[ref].closeModal()
    },
    async onCreated() {
      this.pagination.current_page = 1
      await this.fetchList()
    },
    async get_categories() {
      const result = await form_api_submit({
        url: 'asset/category/dropdown',
        method: 'GET',
        show_success_toast: false,
        show_error_toast: true,
      })

      if (result.success && result.data) {
        this.dropdownCategories = result.data.map(c => ({
          value: c.name,
          label: c.name
        }))
      } else {
        this.dropdownCategories = []
      }
    },

    async get_materials() {
      const result = await form_api_submit({
        url: 'materials/dropdown',
        method: 'GET',
        show_success_toast: false,
        show_error_toast: false,
      })

      if (result.success && result.data) {
        const materialNames = new Set()
        result.data.forEach(category => {
          if (category.get_materials && Array.isArray(category.get_materials)) {
            category.get_materials.forEach(material => {
              if (material.name) {
                materialNames.add(material.name)
              }
            })
          }
        })
        this.dropdownMaterials = Array.from(materialNames).map(name => ({
          value: name,
          label: name
        }))
      } else {
        this.dropdownMaterials = []
      }
    },
    async fetchList() {
      this.loading = true

      const result = await form_api_submit({
        url: 'asset/list',
        method: 'GET',
        data: {
          page: this.pagination.current_page,

          // filters
          name: this.currentFilters.name || '',
          category: this.currentFilters.category || '',
          materials: this.currentFilters.materials || '',

          // sorting
          sort_column: this.sortColumn,
          sort_order: this.sortOrder,
        },
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false
      })

      if (result.success) {
        this.assets = result.data.list
        this.filteredData = result.data.list
        this.pagination = result.data.pagination
      }

      this.loading = false
    },
    initFilters() {
      if (typeof $ === 'undefined' || typeof GenericFilterManager === 'undefined') return

      // Ensure dropdownMaterials is properly formatted before passing to filters
      const categoryOptions = this.dropdownCategories.map(c => ({
        value: c.name || c.id,
        label: c.name || c.label || 'Unnamed'
      }))

      const materialOptions = this.dropdownMaterials.map(m => ({
        value: m.name || m.value || m.id,
        label: m.name || m.label || 'Unnamed'
      }))

      this.filterManager = new GenericFilterManager({
        containerId: 'filtersBar',
        filters: [
          {
            key: 'name',
            label: 'Asset Name',
            type: 'input',
            placeholder: 'Filter by Asset Name'
          },
          {
            key: 'category',
            label: 'Category',
            type: 'checkbox',
            options: categoryOptions
          },
          {
            key: 'materials',
            label: 'Materials',
            type: 'checkbox',
            options: materialOptions
          }
        ],
        onFilterChange: (filters) => {
          this.applyFilters(filters)
        }
      })
    },
    applyFilters(activeFilters) {
      const processedFilters = {}

      for (const [key, value] of Object.entries(activeFilters)) {
        if (Array.isArray(value) && value.length > 0) {
          processedFilters[key] = value.join(',')
        } else if (Array.isArray(value) && value.length === 0) {
          processedFilters[key] = ''
        } else if (value !== undefined && value !== null && value !== '') {
          processedFilters[key] = value
        } else {
          processedFilters[key] = ''
        }
      }

      this.currentFilters = processedFilters
      this.pagination.current_page = 1
      this.fetchList()
    },
    resetAllFilters() {
      if (this.filterManager) {
        this.filterManager.clearAllFilters()
      }

      this.currentFilters = {}
      this.sortColumn = 'name'
      this.sortOrder = 'asc'
      this.pagination.current_page = 1

      this.fetchList()
    },
    changePage(page) {
      if (page < 1 || page > this.pagination.total_pages) return
      this.pagination.current_page = page
      this.fetchList()
    },

    resetPagination() {
      this.pagination.current_page = 1
      this.fetchList()
    },
    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = column
        this.sortOrder = 'asc'
      }
      this.resetPagination()
    },
    getSortIcon(column) {
      if (this.sortColumn !== column) return 'fas fa-sort text-muted'
      return this.sortOrder === 'asc' ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary'
    },
    editAsset(asset) {
      this.editingAsset = asset
      this.$nextTick(() => {
        this.$refs.editModal.showModal()
      })
    },
    viewDetails(asset) {
      this.$router.push({
        name: 'AssetProfile',
        params: {
          external_id: asset.external_id
        }
      })
    }

  }
}
</script>
<style scoped>
:deep(.table-wrapper) {
  width: 100%;
  overflow: visible;
  /* Changed from hidden to visible */
}

:deep(table) {
  width: 100% !important;
  table-layout: fixed !important;
}

/* Keep header cells as is */
:deep(th) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 0;
  vertical-align: middle;
}

/* Asset cell: allow wrapping */
:deep(tbody td.asset-name-cell) {
  white-space: normal;
  overflow: visible;
  max-width: none;
}


.asset-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
  flex-wrap: wrap;
}

/* ── Category badge ── */
.cat-badge {
  display: inline-block;
  font-size: 11px;
  padding: 2px 8px;
  background: #EEEDFE;
  color: #3C3489;
  border-radius: 20px;
  font-weight: 400;
  line-height: 1.4;
}

/* ── Criticality badges ── */
.crit-badge {
  display: inline-block;
  font-size: 11px;
  padding: 2px 10px;
  border-radius: 20px;
  font-weight: 500;
  line-height: 1.6;
}

.crit-high {
  background: #FAECE7;
  color: #712B13;
}

.crit-med {
  background: #FAEEDA;
  color: #633806;
}

.crit-low {
  background: #EAF3DE;
  color: #27500A;
}

/* ── Pagination ── */
.page-link {
  cursor: pointer;
}

.page-item.active .page-link {
  background-color: #667eea;
  border-color: #667eea;
}

img {
  object-fit: cover;
}
</style>